import { useCallback, useEffect, useState } from "react";
import { Check, Image as ImageIcon, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { formatRequestAmount, StatusPill } from "./AdminTransactions";
import useAdminAutoRefresh from "../../lib/useAdminAutoRefresh";

export function PaymentsReview() {
  return <ReviewQueue title="Payments Review" subtitle="Approve customer add-money/load requests." endpoint="/admin/payments-review" kind="load" />;
}

export function PayoutsReview() {
  return <ReviewQueue title="Payouts Review" subtitle="Review customer withdrawal/redeem requests." endpoint="/admin/payouts-review" kind="redeem" />;
}

function ReviewQueue({ title, subtitle, endpoint, kind }) {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [busy, setBusy] = useState({});
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    const q = filter === "all" ? "" : `?status_filter=${filter}`;
    const { data } = await api.get(`${endpoint}${q}`);
    setRows(data);
  }, [endpoint, filter]);

  useEffect(() => { load().catch(() => {}); }, [load]);
  useAdminAutoRefresh(() => load());

  const act = async (row, status) => {
    const label = kind === "redeem" ? "payout" : "payment";
    setBusy((value) => ({ ...value, [row.id]: true }));
    try {
      await api.put(`/admin/requests/${row.id}`, { status, admin_note: "" });
      toast.success(`${label} ${status}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy((value) => ({ ...value, [row.id]: false }));
    }
  };

  return (
    <div className="p-3 sm:p-6 md:p-8">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
          <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="rounded-md border border-white/10 p-2 hover:bg-white/5"><RefreshCw className="h-4 w-4" /></button>
          {["pending", "approved", "rejected", "all"].map((item) => (
            <button
              key={item}
              onClick={() => setFilter(item)}
              className={`rounded-md border px-3 py-1.5 text-xs capitalize ${filter === item ? "border-white bg-white text-black" : "border-white/10 text-slate-300 hover:bg-white/5"}`}
            >
              {item}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-white/10 bg-[#121214]">
        <table className="min-w-[980px] w-full text-sm">
          <thead className="bg-white/5 text-left text-xs uppercase tracking-wider text-slate-400">
            <tr>
              <th className="px-4 py-3">Customer</th>
              <th className="px-4 py-3 text-right">Amount</th>
              <th className="px-4 py-3">Method</th>
              <th className="px-4 py-3">Proof</th>
              <th className="px-4 py-3">Note</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Created</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className="border-t border-white/5">
                <td className="px-4 py-3">
                  <div className="font-medium">{row.username || "-"}</div>
                  <div className="text-xs text-slate-500">{row.email || "-"}</div>
                </td>
                <td className="px-4 py-3 text-right font-semibold tabular-nums">{formatRequestAmount(row)}</td>
                <td className="px-4 py-3 text-xs capitalize text-slate-400">
                  {isGameLoadRow(row) ? (
                    <div>
                      <div>Manual game load</div>
                      <div className="mt-0.5 text-slate-500">{row.game_id || "-"}</div>
                    </div>
                  ) : (
                    row.payment_platform || row.payment_provider || "-"
                  )}
                </td>
                <td className="px-4 py-3">
                  {row.proof_image_url ? (
                    <button onClick={() => setPreview(row)} className="inline-flex items-center gap-1 rounded border border-[#D4AF37]/30 px-2 py-1 text-xs font-semibold text-[#F1C40F] hover:bg-[#D4AF37]/10">
                      <ImageIcon className="h-3.5 w-3.5" />
                      View
                    </button>
                  ) : (
                    <span className="text-xs text-slate-600">-</span>
                  )}
                </td>
                <td className="max-w-xs truncate px-4 py-3 text-xs text-slate-500">{row.note || "-"}</td>
                <td className="px-4 py-3"><StatusPill status={row.status} /></td>
                <td className="px-4 py-3 text-xs text-slate-500">{row.created_at ? new Date(row.created_at).toLocaleString("en-US") : "-"}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {row.status === "pending" ? (
                    <div className="flex justify-end gap-1">
                      <button disabled={busy[row.id]} onClick={() => act(row, "approved")} className="rounded border border-emerald-700/30 p-1.5 text-emerald-300 hover:bg-emerald-500/15"><Check className="h-3.5 w-3.5" /></button>
                      <button disabled={busy[row.id]} onClick={() => act(row, "rejected")} className="rounded border border-red-700/30 p-1.5 text-red-300 hover:bg-red-500/15"><X className="h-3.5 w-3.5" /></button>
                    </div>
                  ) : (
                    <span className="text-xs text-slate-500">{row.admin_note || "-"}</span>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={8} className="py-10 text-center text-slate-500">No items in this queue.</td></tr>}
          </tbody>
        </table>
      </div>
      {preview && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/75 p-4 backdrop-blur-sm" onClick={() => setPreview(null)}>
          <div className="w-full max-w-3xl rounded-2xl border border-white/10 bg-[#121214] p-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold">Payment screenshot</h2>
                <p className="text-xs text-slate-500">{preview.username || preview.email} · {formatRequestAmount(preview)}</p>
              </div>
              <button onClick={() => setPreview(null)} className="rounded border border-white/10 p-2 text-slate-300 hover:bg-white/5"><X className="h-4 w-4" /></button>
            </div>
            <img src={preview.proof_image_url} alt={preview.proof_image_name || "Payment proof"} className="max-h-[70vh] w-full rounded-xl bg-black object-contain" />
          </div>
        </div>
      )}
    </div>
  );
}

function isGameLoadRow(row) {
  return Boolean(
    row?.transfer_kind === "game_load" ||
    row?.manual_game_load ||
    row?.game_id ||
    row?.provider_amount ||
    row?.wallet_debit_amount ||
    row?.free_play_debit_amount
  );
}
